import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import FormControlLabel from '@mui/material/FormControlLabel';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import TextField from '@mui/material/TextField';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { errorMessage } from '@/api/common';
import { mediaNetworkApi } from '@/api/media-network/mediaNetworkApi';
import { mediaNetworkKeys } from '@/api/media-network/mediaNetworkQueries';
import type { Advertiser } from '@/models/media-network/Advertiser';
import { useLocalizedStrings } from '../../../localization/LocalizedStringsProvider';
import { useToast } from '../../common/toast/ToastProvider';
import ReadOnlyFieldset from '../../common/dialog/ReadOnlyFieldset';

interface Props {
    open: boolean;
    advertiser: Advertiser | null;
    // View-only: the grid opens this with an eye rather than a pencil for read access alone.
    readOnly?: boolean;
    onClose: () => void;
}

// Create or edit a TouchTab advertiser — the company itself, not any brand's relationship with it. Which
// clients hold them is the grants dialog; that is a different decision and a different screen.
const AdvertiserDialog = ({ open, advertiser, readOnly, onClose }: Props) => {
    const strings = useLocalizedStrings();
    const toast = useToast();
    const qc = useQueryClient();

    const [name, setName] = useState('');
    const [externalId, setExternalId] = useState('');
    const [enabled, setEnabled] = useState(true);

    useEffect(() => {
        if (!open) return;
        setName(advertiser?.name ?? '');
        setExternalId(advertiser?.externalId ?? '');
        setEnabled(advertiser?.enabled ?? true);
    }, [open, advertiser]);

    const { mutateAsync: save, isPending } = useMutation({
        mutationFn: () => {
            const body = { name: name.trim(), externalId: externalId.trim() || null, enabled };
            return advertiser
                ? mediaNetworkApi.updatePlatformAdvertiser(advertiser.id, body)
                : mediaNetworkApi.createPlatformAdvertiser(body).then(() => undefined);
        },
        onSuccess: async () => {
            toast({ severity: 'success', message: strings.advertiserSaved });
            await qc.invalidateQueries({ queryKey: mediaNetworkKeys.platformAdvertisers() });
            onClose();
        },
        // The name clash is the one worth reading: it means this company is already on the roster.
        onError: (e: unknown) => toast({ severity: 'error', message: errorMessage(e, strings.advertiserSaveError) }),
    });

    return (
        <Dialog open={open} onClose={isPending ? undefined : onClose} maxWidth="sm" fullWidth>
            <DialogTitle>{advertiser ? strings.advertiserEdit : strings.advertiserNew}</DialogTitle>

            <DialogContent dividers>
                <ReadOnlyFieldset readOnly={readOnly}>
                <Stack spacing={2} sx={{ pt: 1 }}>
                    <TextField
                        label={strings.name}
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        disabled={isPending}
                        fullWidth
                    />

                    <TextField
                        label={strings.advertiserAccountRef}
                        helperText={strings.advertiserAccountRefHelp}
                        value={externalId}
                        onChange={(e) => setExternalId(e.target.value)}
                        disabled={isPending}
                        fullWidth
                    />

                    {/* TouchTab's switch, not a brand's. Off here removes them from every client's picker at
                        once; a single brand declining them is done on that brand's own screen. */}
                    <FormControlLabel
                        control={<Switch checked={enabled} onChange={(e) => setEnabled(e.target.checked)} disabled={isPending} />}
                        label={strings.advertiserEnabledEverywhere}
                    />
                </Stack>
            </ReadOnlyFieldset>
            </DialogContent>

            <DialogActions>
                <Button variant="outlined" onClick={onClose} disabled={isPending}>{readOnly ? strings.close : strings.cancel}</Button>
                {!readOnly && <Button
                    variant="contained"
                    onClick={() => save()}
                    loading={isPending}
                    disabled={isPending || !name.trim()}
                >
                    {strings.save}
                </Button>}
            </DialogActions>
        </Dialog>
    );
};

export default AdvertiserDialog;
